import { useCallback } from "react";
import { useHistory } from "react-router-dom";
import { useLogger } from "shared/@hooks/useLogger";

/**
 * A hook to manage query parameters.
 */
export function useQueryParams() {
  const { logTrace } = useLogger({ prefix: "useQueryParams" });
  const history = useHistory();

  const clear = useCallback(() => {
    logTrace("Clearing query params");
    history.push({ search: null });
  }, [history, logTrace]);

  const set = useCallback(
    (object, options = {}) => {
      const { replace = false } = options;
      const params = new URLSearchParams(window.location.search);

      for (const key in object) {
        const value = object[key];

        if (value == null) {
          params.delete(key);
        } else {
          params.set(key, value);
        }
      }

      if (replace) {
        history.replace({ search: params.toString() });
        return;
      }

      history.push({ search: params.toString() });
    },
    [history]
  );

  const get = useCallback((name) => {
    const params = new URLSearchParams(window.location.search);

    // Return the one value if name is passed.
    if (name != null) {
      return params.get(name);
    }

    // Otherwise return an object with all params.
    const q = {};
    for (const [name, value] of params) {
      q[name] = value;
    }

    return q;
  }, []);

  return {
    clear,
    get,
    set,
    toString: () => window.location.search,
  };
}
